import { createContext, useContext } from 'react';

/**
 * What the host app tells the settings about AI settings changing elsewhere.
 * The events carry ids at most and never say who acted, so an agent's own
 * saves come back too; each handler reads the settings again rather than
 * trusting the event to describe the change.
 */
export type AiSettingsEventHandlers = {
  /** The draft was created, or its settings replaced. */
  onDraftUpdated: () => void;
  /** The draft was saved as a new version, which ends the draft. */
  onVersionCreated: (versionId: string) => void;
  /** A version was renamed or made active. */
  onVersionUpdated: (versionId: string) => void;
  /** Events sent while the connection was down never arrived. */
  onReconnected: () => void;
};

/** Starts delivering events to the handlers, and returns what stops it. */
export type SubscribeToAiSettingsEvents = (
  handlers: AiSettingsEventHandlers,
) => () => void;

const AiSettingsEventsContext = createContext<
  SubscribeToAiSettingsEvents | undefined
>(undefined);

/**
 * Supplied by the host app, which alone holds the web socket. Injected via the
 * `subscribeToAiSettingsEvents` prop on the knowledge management entry.
 */
export const AiSettingsEventsProvider = AiSettingsEventsContext.Provider;

export function useSubscribeToAiSettingsEvents():
  | SubscribeToAiSettingsEvents
  | undefined {
  return useContext(AiSettingsEventsContext);
}
